import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AcBadge,
  AcButton,
  ErrorState,
  LoadingState,
  SectionShell,
} from '@/dashboard/ui';
import {
  fetchDoctorEvidenceInbox,
  type DoctorEvidenceInbox,
  type DoctorEvidenceInboxItem,
} from '@/dashboard/api';
import {
  DRAFT_CASE_SHEET_CHIP_LABEL,
  evidenceClassLabel,
  INBOX_CARD_ROW_CAP,
  owedWorkBecauseLine,
  owedWorkRows,
  owedWorkSummary,
  sinceVisitLabel,
  type OwedWorkSummary,
  type DraftCaseSheetRow,
  type InboxPatientCardRow,
  type OwedWorkRow,
} from '@/dashboard/doctor/clinical-evidence-view';
import { consultPathForAppointment } from '@/dashboard/doctor/doctor-home-view';
import { EvidenceCaptureDialog } from '@/dashboard/doctor/ClinicalEvidencePanel';
import './clinical-evidence.css';

/**
 * DOCTOR-OWED-WORK-QUEUE-V1 -- the doctor's OWED WORK, one place across
 * patients (founder ruling Q2, 2026-08-10: EXTEND the accepted evidence
 * inbox rail into "Owed work" -- no new route, no nav change, no new visual
 * language). Two categories, ONE derived-on-read list (the server derives
 * both from a single scan; nothing is stored):
 *
 *   A. DRAFT case sheets  -- the "Case sheet draft" chip (the day worklist's
 *      D52 grammar) + a since-visit age; ONE door = Resume consult, opening
 *      the OLDEST draft via consultPathForAppointment. Finishing (finalize)
 *      makes the row cease to derive on refetch.
 *   B. Owed evidence      -- the pre-existing per-patient owed-evidence row:
 *      counted owed badges + the in-place Capture door, UNCHANGED. A
 *      successful attach makes the row cease to derive on refetch.
 *
 * DR-20 laws hold: one row per patient (per category), newest visit first,
 * capped with a "view all" fold, one honest windowed empty line. The queue
 * only ever OPENS DOORS -- there is no second clinical editor here.
 *
 * Mounts ONLY behind me.clinicalEvidenceEnabled (the per-org surface switch,
 * owned by the router + DoctorPortalPage -- both out of this package's
 * fence). The draft-item DERIVATION is independent of that switch server-
 * side (composition note); making the queue reachable with the switch OFF
 * is a named upgrade in the dossier, not built here.
 */

/**
 * ONE EVIDENCE ROW (category B, DR-20): name chip + owed badges + ONE
 * Capture door. Byte-unchanged from the evidence-inbox rail.
 */
function InboxPatientRow({
  row,
  nowMs,
  onCapture,
}: {
  readonly row: InboxPatientCardRow;
  readonly nowMs: number;
  readonly onCapture: (item: DoctorEvidenceInboxItem) => void;
}): JSX.Element {
  // AGE ON THE CHIP, NOT BESIDE IT (DOCTOR-TODAY-CRAFT-V1). The draft row
  // always showed how long its debt had sat; this row showed none, so two
  // identical debts read differently depending on their kind. The age now
  // rides the FIRST badge -- which is the OLDEST owed item, the one the
  // Capture door opens -- instead of a new span per row, because the count
  // ruling (founder, 2026-09-01) buys facts by consolidation, not addition.
  const since = sinceVisitLabel(row.captureItem.visitDate, nowMs);
  return (
    <div className="doctor-inbox__item" data-testid={`doctor-inbox-row-${row.key}`}>
      <span className="doctor-inbox__item-facts">
        <span className="doctor-inbox__patient">
          {row.patientName ?? 'Patient record pending'}
          {row.patientUmr !== null ? (
            <span className="doctor-inbox__umr">{row.patientUmr}</span>
          ) : null}
        </span>
        {row.owed.map((badge, i) => (
          <AcBadge key={badge.kind} tone="pending">
            {i === 0 && since !== null ? `${badge.label} - ${since}` : badge.label}
          </AcBadge>
        ))}
      </span>
      {/* ONE door per patient: it opens the OLDEST owed item. The title names
          WHICH one, so a multi-item row is never a mystery button. */}
      <AcButton
        size="sm"
        variant="primary"
        onClick={() => onCapture(row.captureItem)}
        data-testid={`doctor-inbox-capture-${row.captureItem.caseSheetId}-${row.captureItem.kind}`}
        title={`Capture ${evidenceClassLabel(row.captureItem.kind)} - visit ${row.captureItem.visitDate}`}
      >
        Capture
      </AcButton>
    </div>
  );
}

/**
 * ONE DRAFT ROW (category A, DR-20): name chip + the "Case sheet draft" chip
 * + since-visit age; ONE door = Resume consult. The door only NAVIGATES --
 * the unfinished sheet is finished in the consult workspace, never here (no
 * second editor).
 */
export function DraftInboxRow({
  row,
  nowMs,
  onResume,
}: {
  readonly row: DraftCaseSheetRow;
  readonly nowMs: number;
  readonly onResume: (item: DoctorEvidenceInboxItem) => void;
}): JSX.Element {
  const since = sinceVisitLabel(row.sinceVisitDate, nowMs);
  return (
    <div
      className="doctor-inbox__item"
      data-testid={`doctor-inbox-draft-row-${row.key}`}
    >
      <span className="doctor-inbox__item-facts">
        <span className="doctor-inbox__patient">
          {row.patientName ?? 'Patient record pending'}
          {row.patientUmr !== null ? (
            <span className="doctor-inbox__umr">{row.patientUmr}</span>
          ) : null}
        </span>
        {/* ONE CHIP, TWO FACTS (DOCTOR-TODAY-CRAFT-V1). The kind and the age
            used to be a chip plus a separate span; the span is folded in, so
            the row loses an element and the evidence row beside it gains the
            same age treatment. Nothing is hidden -- the words are identical. */}
        <AcBadge tone="pending" testId={`doctor-inbox-draft-chip-${row.key}`}>
          {since !== null
            ? `${DRAFT_CASE_SHEET_CHIP_LABEL} - ${since}`
            : DRAFT_CASE_SHEET_CHIP_LABEL}
        </AcBadge>
      </span>
      {/* ONE door: Resume the oldest RESUMABLE draft. It NAVIGATES to the
          consult -- the queue opens doors, it never edits the sheet.

          UNLESS THE VISIT CANNOT BE RESUMED (Lane 1 item 1 / D01). The QA
          walk pressed Resume on a CANCELLED appointment and got "Start signal
          failed" over a Retry that could never succeed. THE SHARED
          eligibility rule now decides, and a blocked row says what happened
          to the visit in place of the door -- the debt stays visible and
          countable, only the futile press is gone (LES-016: never a dead
          door, and never a silent one). */}
      {row.door.kind === 'blocked' ? (
        <span
          className="doctor-inbox__blocked"
          data-testid={`doctor-inbox-blocked-${row.resumeItem.appointmentId}`}
        >
          {row.door.reason}
        </span>
      ) : (
        <AcButton
          size="sm"
          variant="primary"
          onClick={() => onResume(row.resumeItem)}
          data-testid={`doctor-inbox-resume-${row.resumeItem.appointmentId}`}
          title={`Resume consult - visit ${row.resumeItem.visitDate}`}
        >
          Resume consult
        </AcButton>
      )}
    </div>
  );
}

type InboxState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; inbox: DoctorEvidenceInbox };

/**
 * How many owed rows the rail shows before the "View all" fold.
 *
 * WHY THIS IS A PROP AND NOT A CHANGE TO INBOX_CARD_ROW_CAP. That constant is
 * DR-20's ruled cap for this rail and another package pins it at 5
 * (doctor-precall-polish.test.tsx). The founder ruling here is narrower: on
 * the DAY BOARD, where the rail now sits above the day's schedule, five rows
 * turn a doctor's day into an inbox -- "do not bury 29 owed items" is not
 * "let debt occupy half the workspace". So the day board passes its own,
 * smaller cap and the primitive's ruled default is untouched.
 */
export interface DoctorInboxRailProps {
  /**
   * Report this rail's summary UP to the page, so the masthead story can say
   * how much is owed without a SECOND read of the same endpoint. The rail
   * stays the ONE owner of the fetch and the ONE owner of the derivation --
   * the page receives the result, it does not recompute it, so the opening
   * line and this section can never disagree about what is owed.
   */
  readonly onSummary?: (summary: OwedWorkSummary | null) => void;
  /** Rows shown before the "View all N items" fold. Defaults to the DR-20
   *  ruled cap; the day board narrows it (see above). */
  readonly rowCap?: number;
}

export default function DoctorInboxRail({
  onSummary,
  rowCap = INBOX_CARD_ROW_CAP,
}: DoctorInboxRailProps = {}): JSX.Element {
  const navigate = useNavigate();
  // ONE render clock, so the section's "oldest N days" and every row's age
  // are read off the same instant (the follow-ups register's nowMs idiom).
  const nowMs = Date.now();
  const [state, setState] = useState<InboxState>({ kind: 'loading' });
  const [captureFor, setCaptureFor] = useState<DoctorEvidenceInboxItem | null>(
    null,
  );

  const refetch = useCallback(() => {
    fetchDoctorEvidenceInbox()
      .then((inbox) => setState({ kind: 'ready', inbox }))
      .catch((err) =>
        setState({
          kind: 'error',
          message:
            err instanceof Error
              ? err.message
              : 'The queue could not be loaded.',
        }),
      );
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const resume = useCallback(
    (item: DoctorEvidenceInboxItem) => {
      navigate(consultPathForAppointment(item.appointmentId));
    },
    [navigate],
  );

  // The rows and their summary are derived ONCE here (they were derived
  // inside the body before), so the section heading and the list can never
  // disagree about what is owed.
  const rows = state.kind === 'ready' ? owedWorkRows(state.inbox.items) : [];
  const summary = state.kind === 'ready' ? owedWorkSummary(rows, nowMs) : null;
  const becauseLine = summary === null ? null : owedWorkBecauseLine(summary);

  // Publish the summary AFTER render, keyed on its own values -- never during
  // render (that would set state on the parent mid-render) and never on the
  // object identity, which changes every pass and would loop.
  const summaryTotal = summary?.total ?? -1;
  const summaryOldest = summary?.oldestDays ?? null;
  const summaryDrafts = summary?.draftCount ?? -1;
  useEffect(() => {
    if (onSummary === undefined) return;
    onSummary(
      summaryTotal < 0
        ? null
        : {
            total: summaryTotal,
            draftCount: summaryDrafts,
            evidenceCount: summaryTotal - summaryDrafts,
            oldestDays: summaryOldest,
          },
    );
  }, [onSummary, summaryTotal, summaryDrafts, summaryOldest]);

  return (
    <SectionShell
      /* THE EYEBROW IS GONE (founder count ruling, 2026-09-01). "Doctor
         inbox" sat above "Owed work" saying nothing the title did not --
         two headings for one section. The title survives; the label does
         not. */
      title="Owed work"
      /* THE SUB-LINE IS NOW A BECAUSE-LINE, NOT A RESTATEMENT. It used to
         read "One row per patient - resume drafts, capture what is owed",
         which described the rows the doctor was already looking at. It now
         carries figures those rows cannot show at a glance: the split by
         kind, the age of the oldest debt, and the order the list is in.
         Same one element, a fact instead of a description. The full DR-20
         guidance survives verbatim as the tooltip -- zero information loss,
         exactly as before. */
      sub={becauseLine === null ? undefined : (
        <span title="Your unfinished work across patients: draft case sheets to resume, and visits still missing their case-sheet photos or consent scan to capture. Each item clears itself when you finish it.">
          {becauseLine}
        </span>
      )}
      testId="doctor-inbox-rail"
    >
      {state.kind === 'loading' ? (
        <LoadingState label="Checking what is owed..." lines={2} />
      ) : state.kind === 'error' ? (
        <ErrorState
          title="The queue could not be loaded."
          detail={state.message}
          action={
            <AcButton size="sm" variant="secondary" onClick={refetch}>
              Try again
            </AcButton>
          }
        />
      ) : (
        (() => {
          if (rows.length === 0) {
            // ONE HONEST LINE (DR-20, S-4). No panel, no illustration.
            return (
              <p className="doctor-inbox__empty" data-testid="doctor-inbox-empty">
                Nothing owed - the last {state.inbox.windowDays} days are
                complete.
              </p>
            );
          }
          const shown = rows.slice(0, rowCap);
          const rest = rows.slice(rowCap);
          const renderRow = (r: OwedWorkRow): JSX.Element =>
            r.rowType === 'draft' ? (
              <DraftInboxRow
                key={r.key}
                row={r.draft}
                nowMs={nowMs}
                onResume={resume}
              />
            ) : (
              <InboxPatientRow
                key={r.key}
                row={r.evidence}
                nowMs={nowMs}
                onCapture={setCaptureFor}
              />
            );
          return (
            <div className="doctor-inbox" data-testid="doctor-inbox-list">
              {shown.map(renderRow)}
              {/* VIEW ALL (DR-20): the capped rows stay REACHABLE -- nothing
                  is deleted, the card just stops being a scroll (the
                  Done-today expander idiom, reused). */}
              {rest.length > 0 ? (
                <details
                  className="doctor-inbox__more"
                  data-testid="doctor-inbox-view-all"
                >
                  <summary>View all {rows.length} items</summary>
                  {rest.map(renderRow)}
                </details>
              ) : null}
            </div>
          );
        })()
      )}

      {captureFor !== null ? (
        <EvidenceCaptureDialog
          title="Capture evidence"
          caseSheetId={captureFor.caseSheetId}
          fixedClass={captureFor.kind}
          onAttached={refetch}
          onClose={() => {
            setCaptureFor(null);
            refetch();
          }}
        />
      ) : null}
    </SectionShell>
  );
}
